import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TERMS_DATA } from '../../content/terms';
import type { TermsType } from '../../types/terms';

interface Props {
  open: boolean;
  termsType: TermsType | null;
  onClose: () => void;
}

const TermsViewerModal: React.FC<Props> = ({ open, termsType, onClose }) => {
  const termsData = termsType ? TERMS_DATA[termsType] : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="terms-dialog-title"
    >
      <DialogTitle
        id="terms-dialog-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {termsData?.title ?? ''}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="닫기">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 2 }}>
        {termsData && (
          <Box
            sx={{
              fontSize: '1rem',
              lineHeight: 1.9,
              color: 'text.primary',
              '& h2': { fontSize: '1.15rem', fontWeight: 700, mt: 3, mb: 1 },
              '& h3': { fontSize: '1.05rem', fontWeight: 600, mt: 2, mb: 0.5 },
              '& p': { mb: 1.5 },
              '& table': { width: '100%', borderCollapse: 'collapse', my: 2, fontSize: '0.9rem' },
              '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 1, textAlign: 'left' },
              '& th': { bgcolor: 'action.hover', fontWeight: 600 },
              '& ul, & ol': { pl: 3, mb: 1.5 },
              '& li': { mb: 0.5 },
              '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, my: 1.5, color: 'text.secondary' },
              '& strong': { fontWeight: 600 },
              '& hr': { my: 3, border: 'none', borderTop: '1px solid', borderColor: 'divider' },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{termsData.content}</ReactMarkdown>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          onClick={onClose}
          fullWidth
          sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsViewerModal;
